import {
  IBaselineCoverageRow,
  IForecastBaselineBatch,
  IForecastingBatchRow,
  IForecastingContext,
  IForecastingManager,
  IForecastingStage,
  IForecastingWorkbench,
  IForecastQualityIssue,
  IForecastResultRow,
  IForecastVariableRow,
  ForecastingStageState,
  ForecastingStepCode,
} from './bieforecasting.models';

export const asBoolean = (value: unknown, fallback = false): boolean => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return ['1', 'true', 'yes', 'y'].includes(String(value).toLowerCase());
};

export const asNumber = (value: unknown, fallback = 0): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const nullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const unwrap = (raw: any): any => {
  const envelope = raw?.sd ?? raw ?? {};
  return envelope?.sd ?? envelope ?? {};
};

export const normalizeForecastingContext = (r: any): IForecastingContext => ({
  planningcycleid: asNumber(r?.planningcycleid ?? r?.pci),
  planningcycleix: r?.planningcycleix ?? r?.pcx,
  planningcyclecode: r?.planningcyclecode ?? r?.pcc ?? '',
  planningcyclename: r?.planningcyclename ?? r?.pcn ?? '',
  fiscalyear: String(r?.fiscalyear ?? r?.fiy ?? r?.fyr ?? ''),
  budgetperiodname: r?.budgetperiodname ?? r?.bpn,
  phasecode: r?.phasecode ?? r?.phc ?? 'FORECASTING',
  historicaldatasetid: asNumber(r?.historicaldatasetid ?? r?.hdi) || undefined,
  historicaldatasetix: r?.historicaldatasetix ?? r?.hdx,
  historicaldatasetcode: r?.historicaldatasetcode ?? r?.hdc ?? '',
  historicaldatasetname: r?.historicaldatasetname ?? r?.hdn ?? '',
  coveragesource: r?.coveragesource ?? r?.cvs ?? '',
  observedcoveragestartdate: nullableString(r?.observedcoveragestartdate ?? r?.ocs),
  observedcoverageenddate: nullableString(r?.observedcoverageenddate ?? r?.oce),
  expectedhistoricalstartdate: nullableString(r?.expectedhistoricalstartdate ?? r?.ehs),
  expectedhistoricalenddate: nullableString(r?.expectedhistoricalenddate ?? r?.ehe),
  coveragecontractready: asBoolean(r?.coveragecontractready ?? r?.ccr),
  forecaststartdate: nullableString(r?.forecaststartdate ?? r?.fsd),
  forecastenddate: nullableString(r?.forecastenddate ?? r?.fed),
});

export const normalizeForecastingBatchRow = (r: any): IForecastingBatchRow => ({
  rid: asNumber(r?.rid ?? r?.recid ?? r?.id),
  rix: r?.rix ?? r?.recix,
  shc: r?.shc ?? r?.shortcode ?? r?.batchcode ?? '',
  nam: r?.nam ?? r?.recname ?? r?.batchname ?? '',
  pci: asNumber(r?.pci ?? r?.planningcycleid),
  fiy: String(r?.fiy ?? r?.fiscalyear ?? ''),
  hms: asNumber(r?.hms ?? r?.historymonths, 60),
  fsd: nullableString(r?.fsd ?? r?.forecaststartdate),
  fed: nullableString(r?.fed ?? r?.forecastenddate),
  ssc: r?.ssc ?? r?.statecode ?? '',
  ssn: r?.ssn ?? r?.statename ?? '',
  cri: asNumber(r?.cri ?? r?.creatorid) || null,
  pbi: asNumber(r?.pbi ?? r?.publisherid) || null,
  pat: nullableString(r?.pat ?? r?.publishedat),
  dcd: nullableString(r?.dcd ?? r?.created),
  used: asBoolean(r?.used),
  deletable: asBoolean(r?.deletable),
  openmode: String(r?.openmode ?? 'EDIT').toUpperCase() === 'VIEW' ? 'VIEW' : 'EDIT',
});

export const normalizeForecastingManager = (raw: any): IForecastingManager => {
  const r = unwrap(raw);
  const rows = Array.isArray(r?.batches) ? r.batches : [];
  return {
    context: normalizeForecastingContext(r?.context ?? {}),
    summary: {
      batchcount: asNumber(r?.summary?.batchcount),
      draftcount: asNumber(r?.summary?.draftcount),
      generatedcount: asNumber(r?.summary?.generatedcount),
      qualityvalidatedcount: asNumber(r?.summary?.qualityvalidatedcount),
      publishedcount: asNumber(r?.summary?.publishedcount),
      usablecount: asNumber(r?.summary?.usablecount),
    },
    batches: rows.map(normalizeForecastingBatchRow),
    actions: {
      canRefresh: asBoolean(r?.actions?.canRefresh, true),
      canCreateBatch: asBoolean(r?.actions?.canCreateBatch, true),
      canComplete: asBoolean(r?.actions?.canComplete),
      nextpage: nullableString(r?.actions?.nextpage),
    },
  };
};

export const normalizeCoverageRow = (r: any): IBaselineCoverageRow => ({
  variablecode: r.variablecode ?? r.vrc ?? r.code ?? '',
  variablename: r.variablename ?? r.vrn ?? r.name ?? '',
  datasetcode: r.datasetcode ?? r.dsc ?? '',
  required: asBoolean(r.required ?? r.req, true),
  forecastable: asBoolean(r.forecastable ?? r.fca, true),
  source: r.source ?? r.src ?? 'FACT',
  continuousfrom: r.continuousfrom ?? r.cfr ?? '',
  continuousto: r.continuousto ?? r.cto ?? '',
  forecaststart: r.forecaststart ?? r.fst ?? '',
  readiness: asNumber(r.readiness ?? r.rdn),
  status: r.status ?? r.sts ?? 'WARNING',
  missingperiods: r.missingperiods ?? r.msp ?? [],
  periods: (r.periods ?? r.prd ?? []).map((p: any) => ({
    periodcode: p.periodcode ?? p.pdc ?? '',
    periodname: p.periodname ?? p.pdn ?? p.periodcode ?? p.pdc ?? '',
    startdate: p.startdate ?? p.sdt,
    enddate: p.enddate ?? p.edt,
    status: p.status ?? p.sts ?? 'MISSING',
    approved: asBoolean(p.approved ?? p.apv),
    factcount: asNumber(p.factcount ?? p.fct),
    amount: asNumber(p.amount ?? p.amt),
    remarks: p.remarks ?? p.rmk ?? '',
  })),
});

export const normalizeVariableRow = (r: any): IForecastVariableRow => ({
  id: r.id ?? r.rid ?? r.recid ?? r.variablecode ?? r.vrc,
  rid: asNumber(r.rid ?? r.recid) || undefined,
  rix: r.rix ?? r.recix,
  variablecode: r.variablecode ?? r.vrc ?? r.shortcode ?? r.shc ?? '',
  variablename: r.variablename ?? r.vrn ?? r.recname ?? r.nam ?? '',
  datasetcode: r.datasetcode ?? r.dsc ?? '',
  forecastmethod: r.forecastmethod ?? r.fmd ?? 'MANAGEMENT_ESTIMATE',
  source: r.source ?? r.src ?? 'FACT',
  required: asBoolean(r.required ?? r.req, true),
  forecastable: asBoolean(r.forecastable ?? r.fca, true),
  effectivefrom: r.effectivefrom ?? r.efr ?? '',
  effectiveto: r.effectiveto ?? r.eto ?? '',
  growthrate: r.growthrate == null ? null : asNumber(r.growthrate ?? r.grt),
  fixedvalue: r.fixedvalue == null ? null : asNumber(r.fixedvalue ?? r.fvl),
  seasonality: asBoolean(r.seasonality ?? r.sea),
  recommendedmethod: r.recommendedmethod ?? r.rmd,
  recommendationconfidence: r.recommendationconfidence ?? r.rcf,
  recommendationreason: r.recommendationreason ?? r.rrs ?? '',
  recommendationsamplecount: asNumber(r.recommendationsamplecount ?? r.rsc),
  recommendationdiagnostics: r.recommendationdiagnostics ?? r.rdg ?? {},
  overrideReason: r.overrideReason ?? r.overridereason ?? r.ovr ?? '',
  status: r.status ?? r.sts ?? 'ACTIVE',
  stamp: r.stamp ?? r.stp,
});

export const normalizeForecastResultRow = (r: any): IForecastResultRow => ({
  id: r.id ?? r.rid ?? r.recid ?? r.rix ?? `${r.variablecode ?? r.vrc}-${r.periodcode ?? r.pdc}`,
  rid: asNumber(r.rid ?? r.recid) || undefined,
  rix: r.rix ?? r.recix,
  variablecode: r.variablecode ?? r.vrc ?? r.metriccode ?? r.mtc ?? '',
  variablename: r.variablename ?? r.vrn ?? r.metricname ?? r.mtn ?? '',
  productcode: r.productcode ?? r.prc,
  productname: r.productname ?? r.prn,
  locationcode: r.locationcode ?? r.lcc,
  locationname: r.locationname ?? r.lcn,
  periodcode: r.periodcode ?? r.pdc ?? '',
  periodname: r.periodname ?? r.pdn ?? r.periodcode ?? r.pdc ?? '',
  source: r.source ?? r.src ?? 'FORECAST',
  method: r.method ?? r.mth ?? r.forecastmethod ?? r.fmd ?? r.calculationmode,
  quantity: r.quantity == null ? null : asNumber(r.quantity ?? r.qty),
  unitprice: r.unitprice == null ? null : asNumber(r.unitprice ?? r.upr ?? r.rate),
  amount: r.amount == null ? null : asNumber(r.amount ?? r.amt),
  confidence: r.confidence == null ? null : asNumber(r.confidence ?? r.cnf),
  status: r.status ?? r.sts ?? 'GENERATED',
  warningcount: asNumber(r.warningcount ?? r.wrn),
  errorcount: asNumber(r.errorcount ?? r.err),
  remarks: r.remarks ?? r.rmk ?? r.notes,
  created: r.created ?? r.crt,
  stamp: r.stamp ?? r.stp,
});

export const normalizeForecastQualityIssue = (r: any): IForecastQualityIssue => ({
  id: r.id ?? r.rid ?? r.recid ?? r.rix ?? `${r.issuecode ?? r.isc}-${r.periodcode ?? r.pdc}-${r.variablecode ?? r.vrc}`,
  rid: asNumber(r.rid ?? r.recid) || undefined,
  rix: r.rix ?? r.recix,
  validationix: r.validationix ?? r.vix,
  severity: r.severity ?? r.sev ?? 'INFO',
  issuecode: r.issuecode ?? r.isc ?? '',
  message: r.message ?? r.msg ?? r.description ?? '',
  variablecode: r.variablecode ?? r.vrc,
  productcode: r.productcode ?? r.prc,
  locationcode: r.locationcode ?? r.lcc,
  periodcode: r.periodcode ?? r.pdc,
  actualvalue: r.actualvalue == null ? null : asNumber(r.actualvalue ?? r.avl),
  expectedvalue: r.expectedvalue == null ? null : asNumber(r.expectedvalue ?? r.evl),
  status: r.status ?? r.sts ?? 'OPEN',
  resolution: r.resolution ?? r.rsl,
  created: r.created ?? r.crt,
});

export const normalizeBatch = (r: any): IForecastBaselineBatch | null => {
  if (!r || !(r.rid ?? r.recid ?? r.id)) return null;
  return {
    id: r.id ?? r.rid ?? r.recid,
    rid: asNumber(r.rid ?? r.recid ?? r.id),
    rix: r.rix ?? r.recix,
    batchcode: r.batchcode ?? r.btc ?? r.shortcode ?? r.shc ?? '',
    batchname: r.batchname ?? r.btn ?? r.recname ?? r.nam ?? '',
    fiscalyear: String(r.fiscalyear ?? r.fiy ?? r.fyr ?? ''),
    historymonths: asNumber(r.historymonths ?? r.hms, 60),
    forecaststartdate: nullableString(r.forecaststartdate ?? r.fsd ?? r.forecaststart),
    forecastenddate: nullableString(r.forecastenddate ?? r.fed),
    statecode: r.statecode ?? r.stc ?? r.ssc ?? '',
    statename: r.statename ?? r.stn ?? r.ssn ?? '',
    creatorid: asNumber(r.creatorid ?? r.cri) || null,
    publisherid: asNumber(r.publisherid ?? r.pbi) || null,
    created: r.created ?? r.crt ?? r.dcd,
    publishedat: nullableString(r.publishedat ?? r.pat),
    publicationnotes: nullableString(r.publicationnotes ?? r.pno),
  };
};

export const normalizeWorkbench = (raw: any): IForecastingWorkbench => {
  const r = unwrap(raw);
  const sourceStages = r.stages ?? r.stg ?? {};
  const normalizeCode = (value: unknown): string => String(value ?? '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const aliases: Record<string, string[]> = {
    setup: ['SETUP', 'FORECAST_SETUP', 'BATCH_SETUP'],
    integrity: ['INTEGRITY', 'BASELINE_INTEGRITY', 'HISTORICAL_INTEGRITY', 'BASELINE_VALIDATION'],
    variables: ['VARIABLES', 'FORECAST_VARIABLES', 'VARIABLE_CONFIGURATION', 'CONFIGURE_VARIABLES'],
    forecast: ['FORECAST', 'GENERATE_FORECAST', 'FORECAST_GENERATION', 'GENERATION'],
    quality: ['QUALITY', 'QUALITY_REVIEW', 'FORECAST_QUALITY', 'QUALITY_VALIDATION'],
    publish: ['PUBLISH', 'PUBLICATION', 'PUBLISH_FORECAST'],
  };
  const stageRows: any[] = Array.isArray(sourceStages) ? sourceStages : Object.entries(sourceStages).map(([key, value]) => ({ ...(value && typeof value === 'object' ? value : {}), __key: key }));
  const findStage = (code: string): any => {
    const accepted = new Set((aliases[code] ?? [code]).map(normalizeCode));
    return stageRows.find((item: any) => accepted.has(normalizeCode(item?.code ?? item?.stagecode ?? item?.stepcode ?? item?.shc ?? item?.cde ?? item?.__key))) ?? {};
  };
  const normalizeState = (value: unknown): ForecastingStageState => {
    const state = normalizeCode(value);
    if (['OPEN', 'AVAILABLE', 'ACTIVE', 'READY'].includes(state)) return 'AVAILABLE';
    if (['DONE', 'COMPLETE', 'COMPLETED'].includes(state)) return 'COMPLETE';
    if (state === 'ERROR') return 'BLOCKED';
    if (['LOCKED', 'IN_PROGRESS', 'BLOCKED', 'STALE'].includes(state)) return state as ForecastingStageState;
    return 'LOCKED';
  };
  const stage = (code: ForecastingStepCode): IForecastingStage => {
    const item = findStage(code);
    return {
      code,
      state: normalizeState(item?.state ?? item?.statecode ?? item?.sts ?? item?.ssc),
      blockingcount: asNumber(item?.blockingcount ?? item?.blk),
      warningcount: asNumber(item?.warningcount ?? item?.wrn),
      requirement: item?.requirement ?? item?.req ?? item?.description ?? item?.dsc,
    };
  };
  const currentStep = String(r.currentstep ?? r.cst ?? 'setup').toLowerCase() as ForecastingStepCode;
  return {
    context: normalizeForecastingContext(r.context ?? {}),
    currentstep: currentStep,
    stages: { setup: stage('setup'), integrity: stage('integrity'), variables: stage('variables'), forecast: stage('forecast'), quality: stage('quality'), publish: stage('publish') },
    readiness: {
      score: asNumber(r.readiness?.score ?? r.readiness?.rdn),
      blockingcount: asNumber(r.readiness?.blockingcount ?? r.readiness?.blk),
      warningcount: asNumber(r.readiness?.warningcount ?? r.readiness?.wrn),
      factcount: asNumber(r.readiness?.factcount ?? r.readiness?.fct),
      historymonths: asNumber(r.readiness?.historymonths ?? r.readiness?.hms, 60),
      historyfromdate: nullableString(r.readiness?.historyfromdate ?? r.readiness?.hfd),
      historytodate: nullableString(r.readiness?.historytodate ?? r.readiness?.htd),
      canForecast: asBoolean(r.readiness?.canForecast ?? r.readiness?.cfr),
      canValidateQuality: asBoolean(r.readiness?.canValidateQuality ?? r.readiness?.cvq),
      canPublish: asBoolean(r.readiness?.canPublish ?? r.readiness?.cpb),
      canExit: asBoolean(r.readiness?.canExit ?? r.readiness?.cex),
    },
    actions: {
      canRefresh: asBoolean(r.actions?.canRefresh ?? r.actions?.crf, true),
      canCreateBatch: asBoolean(r.actions?.canCreateBatch ?? r.actions?.ccb),
      canDeleteBatch: asBoolean(r.actions?.canDeleteBatch ?? r.actions?.cdb),
      canConfigureVariables: asBoolean(r.actions?.canConfigureVariables ?? r.actions?.ccv),
      canValidateBaseline: asBoolean(r.actions?.canValidateBaseline ?? r.actions?.cvb),
      canGenerate: asBoolean(r.actions?.canGenerate ?? r.actions?.cgn),
      canValidateQuality: asBoolean(r.actions?.canValidateQuality ?? r.actions?.cvq),
      canPublish: asBoolean(r.actions?.canPublish ?? r.actions?.cpb),
      canExit: asBoolean(r.actions?.canExit ?? r.actions?.cex),
      nextpage: nullableString(r.actions?.nextpage ?? r.actions?.npg),
      canOpenSimulation: asBoolean(r.actions?.canOpenSimulation ?? r.actions?.cos),
    },
    coverage: (r.coverage ?? []).map(normalizeCoverageRow),
    variables: (r.variables ?? []).map(normalizeVariableRow),
    batches: (r.batches ?? []).map(normalizeForecastingBatchRow),
    batch: normalizeBatch(r.batch),
    execution: r.execution ? {
      runid: asNumber(r.execution.runid ?? r.execution.rid) || undefined,
      runix: r.execution.runix ?? r.execution.rix,
      statecode: r.execution.statecode ?? r.execution.stc ?? 'NOT_STARTED',
      totalrows: asNumber(r.execution.totalrows ?? r.execution.tot),
      generatedrows: asNumber(r.execution.generatedrows ?? r.execution.gen),
      preservedfacts: asNumber(r.execution.preservedfacts ?? r.execution.pfv),
      warningcount: asNumber(r.execution.warningcount ?? r.execution.wrn),
      errorcount: asNumber(r.execution.errorcount ?? r.execution.err),
      startedat: r.execution.startedat ?? r.execution.sat,
      completedat: r.execution.completedat ?? r.execution.cat,
      executedby: r.execution.executedby ?? r.execution.eby,
      stale: asBoolean(r.execution.stale ?? r.execution.stl),
    } : null,
    quality: {
      status: r.quality?.status ?? r.quality?.sts ?? 'DRAFT',
      confidence: asNumber(r.quality?.confidence ?? r.quality?.cnf),
      exceptions: asNumber(r.quality?.exceptions ?? r.quality?.exc),
      outliers: asNumber(r.quality?.outliers ?? r.quality?.otl),
      interpretation: r.quality?.interpretation ?? r.quality?.int,
      validatedat: r.quality?.validatedat ?? r.quality?.vat,
      validationid: asNumber(r.quality?.validationid ?? r.quality?.vid) || undefined,
      validationix: r.quality?.validationix ?? r.quality?.vix,
      validatedrunix: r.quality?.validatedrunix ?? r.quality?.vrx,
      factstamp: r.quality?.factstamp ?? r.quality?.fst,
      stale: asBoolean(r.quality?.stale ?? r.quality?.stl),
    },
    publication: {
      publicationid: asNumber(r.publication?.publicationid ?? r.publication?.pid) || undefined,
      publicationix: r.publication?.publicationix ?? r.publication?.pix,
      published: asBoolean(r.publication?.published ?? r.publication?.pub),
      publishedat: r.publication?.publishedat ?? r.publication?.pat,
      publishedby: r.publication?.publishedby ?? r.publication?.pby,
      notes: r.publication?.notes ?? r.publication?.nts,
      immutable: asBoolean(r.publication?.immutable ?? r.publication?.imm),
    },
  };
};
